import { useState } from "react"
import { Badge, Card, CardBody, Col, Label, Row, Progress, Table } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import apiClient from "api/axios"
import { useEmployees } from "hooks/employeeHook"
import { useEmployeeKpiBreakdown } from "hooks/useEmployeeKpiBreakdown"

const listFrom = response => (response && response.data && response.data.data) || (response && response.data) || []

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" },
  { value: 3, label: "March" }, { value: 4, label: "April" },
  { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" }, { value: 12, label: "December" },
]

const buildYearOptions = () => {
  const current = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => current - 2 + i)
}

const scoreColor = score => {
  if (score === null || score === undefined) return "secondary"
  if (score >= 80) return "success"
  if (score >= 60) return "warning"
  return "danger"
}

const EmployeeKpiDetailReport = () => {
  document.title = "Employee KPI Detail Report"

  const today = new Date()
  const [employeeId, setEmployeeId] = useState("")
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [year, setYear] = useState(today.getFullYear())
  const [yearOptions] = useState(buildYearOptions())
  const yearSelectOptions = yearOptions.map(y => ({ value: y, label: String(y) }))
  const [isDownloading, setIsDownloading] = useState(false)

  const { data: employeesData, isLoading: employeesLoading } = useEmployees({ all: true })
  const employees = listFrom(employeesData)
  const employeeOptions = employees.map(emp => ({
    value: emp.id,
    label: emp.employee_id ? `${emp.employee_id} - ${emp.name}` : emp.name,
  }))

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEmployeeKpiBreakdown(employeeId, month, year)

  const report = data?.data
  const metrics = report?.metrics ?? []

  const downloadPdf = async () => {
    if (!employeeId) return
    setIsDownloading(true)
    try {
      const res = await apiClient.get(`/kpi/employees/${employeeId}/breakdown/pdf`, {
        params: { month, year },
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      const code = report?.employee?.employee_id ?? employeeId
      link.setAttribute("download", `employee-kpi-${code}-${year}-${String(month).padStart(2, "0")}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Employee KPI report PDF download failed", err)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <PageWrapper title="Employee KPI Detail Report">
      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-end">
            <Col md={3}>
              <Label>Employee</Label>
              <Select
                options={employeeOptions}
                value={employeeOptions.find(o => o.value === Number(employeeId)) || null}
                onChange={selected => setEmployeeId(selected ? selected.value : "")}
                isClearable
                isLoading={employeesLoading}
                placeholder="Search employee…"
              />
            </Col>
            <Col md={2}>
              <Label>Month</Label>
              <Select
                options={MONTHS}
                value={MONTHS.find(m => m.value === month) || null}
                onChange={selected => setMonth(selected ? selected.value : month)}
                placeholder="Search month…"
              />
            </Col>
            <Col md={2}>
              <Label>Year</Label>
              <Select
                options={yearSelectOptions}
                value={yearSelectOptions.find(y => y.value === year) || null}
                onChange={selected => setYear(selected ? selected.value : year)}
                placeholder="Search year…"
              />
            </Col>
            <Col md={2}>
              <button
                className="btn btn-primary"
                onClick={() => refetch()}
                disabled={isFetching || !employeeId}
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </button>
            </Col>
            <Col className="text-end">
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={downloadPdf}
                disabled={isDownloading || !employeeId}
              >
                <i className="bx bx-download me-1"></i>
                {isDownloading ? "Downloading…" : "Download PDF"}
              </button>
            </Col>
          </Row>
          {error && (
            <div className="alert alert-danger mt-3 mb-0 small">
              Failed to load KPI report. {error?.response?.data?.message ?? error?.message}
            </div>
          )}
        </CardBody>
      </Card>

      {!employeeId && (
        <div className="text-center text-muted py-4">Select an employee to view their KPI report.</div>
      )}

      {employeeId && isLoading && !report && (
        <div className="text-center text-muted py-4">Loading KPI report…</div>
      )}

      {employeeId && report && (
        <>
          <Row className="mb-3">
            <Col md={4}>
              <Card>
                <CardBody className="text-center">
                  <div className="fs-2 fw-bold">
                    {report.composite_score !== null ? (
                      <Badge color={scoreColor(report.composite_score)} pill>
                        {report.composite_score}
                      </Badge>
                    ) : "—"}
                  </div>
                  <div className="text-muted small">
                    Composite Score — {report.employee?.name}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody className="text-center">
                  <div className="fs-2 fw-bold">{metrics.filter(m => m.is_applicable).length}</div>
                  <div className="text-muted small">Applicable Metrics</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody className="text-center">
                  <div className="fs-2 fw-bold">{metrics.filter(m => !m.is_applicable).length}</div>
                  <div className="text-muted small">Not Applicable</div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card>
            <CardBody className="p-0">
              <div style={{ overflowX: "auto" }}>
                <Table responsive bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th className="text-center">Weight</th>
                      <th className="text-center">Raw Value</th>
                      <th className="text-center">Peer Avg</th>
                      <th className="text-center">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.map(metric => (
                      <tr key={metric.key}>
                        <td>
                          {metric.name}
                          <br /><span className="text-muted fs-10">{metric.module_slug}</span>
                        </td>
                        <td className="text-center">{metric.weight}</td>
                        <td className="text-center">
                          {metric.is_applicable ? metric.raw_value : "—"}
                        </td>
                        <td className="text-center">
                          {metric.is_applicable && metric.peer_average !== null ? metric.peer_average : "—"}
                        </td>
                        <td className="text-center" style={{ minWidth: 140 }}>
                          {metric.is_applicable ? (
                            <div className="d-flex align-items-center gap-2">
                              <Progress
                                value={metric.normalized_score ?? 0}
                                color={scoreColor(metric.normalized_score)}
                                style={{ height: 6, flex: 1 }}
                              />
                              <span className="small">{metric.normalized_score}</span>
                            </div>
                          ) : (
                            <Badge color="secondary" pill>N/A</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                    {metrics.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          No KPI data for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </PageWrapper>
  )
}

export default EmployeeKpiDetailReport
